import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Router, Route, Switch, useLocation } from 'wouter'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { AxiosError } from 'axios'
import * as Sentry from '@sentry/browser'

import { Login } from '@/templates/Login'
import { Welcome } from '@/templates/Welcome'
import { TeamMembers } from '@/pages/TeamMembers'
import { CreateLink } from '@/pages/CreateLink'
import { Home } from '@/pages/Home'
import { Organization } from '@/pages/Organization'
import { Account } from '@/pages/Account'
import { EditLink } from '@/pages/EditLink'
import { ErrorPage } from '@/pages/Error'
import { Spinner } from '@/components/Spinner'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ToastProvider } from '@/hooks/Toast'
import { useMe } from '@/hooks/useMe'
import { api } from '@/utils/api'

const App = () => {
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const { data: me }: any = useMe(isLoggedIn)

  // const storageLoggedIn = !!window.localStorage.getItem('loggedIn')
  const [, setLocation] = useLocation()
  const queryClient = useQueryClient()

  const login = async (credential: string) => {
    try {
      const url = new URL(window.location.href)
      const refParam = url.searchParams.get('ref')
      const deviceParam = url.searchParams.get('device')
      const referrerParam = url.searchParams.get('referrer')

      window.history.replaceState({}, document.title, url.pathname)

      setIsLoggingIn(true)
      const response = await api.post('/auth/login/google', {
        credential: credential,
        ref: refParam,
        device: deviceParam,
        referrer: referrerParam,
      })

      if (response.data.data?.isNewUser) {
        setLocation('/welcome')
      }
      queryClient.invalidateQueries()

      setIsLoggedIn(true)
      // window.localStorage.setItem('loggedIn', 'true')
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'auth')
        scope.setContext('Login via website', { user: 'unknown' })
        Sentry.captureException(error)
      })
    } finally {
      setIsLoggingIn(false)
    }
  }

  useEffect(() => {
    const url = new URL(window.location.href)
    const value = url.searchParams.get('cred') || url.searchParams.get('code')
    const ref = url.searchParams.get('ref')

    if (ref === 'landing') {
      // window.localStorage.setItem('loggedIn', 'true')
      window.history.replaceState({}, document.title, url.pathname)
    }

    // value ? login(value) : setIsLoggedIn(ref === 'landing' || storageLoggedIn)
    value ? login(value) : setIsLoggedIn(true)
  }, [])

  if (isLoggingIn) return <Spinner />

  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <ToastProvider>
          {me && (me as AxiosError).response?.status === 401 ? (
            <Login />
          ) : (
            <Router>
              <Switch>
                <Route path="/" component={Home} />
                <Route path="/create/:slug?" component={CreateLink} />
                <Route path="/edit/:linkId" component={EditLink} />
                <Route path="/organization/:tab?" component={Organization} />
                <Route path="/:teamId/members" component={TeamMembers} />
                <Route path="/account" component={Account} />
                <Route path="/login" component={Login} />
                <Route path="/welcome" component={Welcome} />
                <Route component={ErrorPage} />
              </Switch>
            </Router>
          )}
        </ToastProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  )
}

export default App
